/** localStorage draft for /survey/prevbrandanswer. */
export const PREV_BRAND_ANSWER_STORAGE_KEY = 'ldsurvey:prevBrandAnswer';

export type ElaboratedReason = {
  checked: boolean;
  elaboration: string;
};

export type PrevBrandAnswerAnswers = {
  taste: ElaboratedReason;
  price: ElaboratedReason;
  productFeatures: ElaboratedReason;
  brandCredibility: boolean;
  peersSmoking: boolean;
  otherReason: string;
  smokedOtherProduct: '' | 'yes' | 'no';
};

const emptyElaborated = (): ElaboratedReason => ({ checked: false, elaboration: '' });

export const emptyPrevBrandAnswerAnswers = (): PrevBrandAnswerAnswers => ({
  taste: emptyElaborated(),
  price: emptyElaborated(),
  productFeatures: emptyElaborated(),
  brandCredibility: false,
  peersSmoking: false,
  otherReason: '',
  smokedOtherProduct: '',
});

function parseElaborated(value: unknown): ElaboratedReason {
  if (typeof value !== 'object' || value === null) return emptyElaborated();
  const o = value as Record<string, unknown>;
  return {
    checked: o.checked === true,
    elaboration: typeof o.elaboration === 'string' ? o.elaboration : '',
  };
}

export function readPrevBrandAnswerAnswers(): PrevBrandAnswerAnswers {
  try {
    const raw = localStorage.getItem(PREV_BRAND_ANSWER_STORAGE_KEY);
    if (!raw) return emptyPrevBrandAnswerAnswers();
    const parsed = JSON.parse(raw) as unknown;
    if (typeof parsed !== 'object' || parsed === null) return emptyPrevBrandAnswerAnswers();
    const o = parsed as Record<string, unknown>;
    const smoked =
      o.smokedOtherProduct === 'yes' || o.smokedOtherProduct === 'no' ? o.smokedOtherProduct : '';
    return {
      taste: parseElaborated(o.taste),
      price: parseElaborated(o.price),
      productFeatures: parseElaborated(o.productFeatures),
      brandCredibility: o.brandCredibility === true,
      peersSmoking: o.peersSmoking === true,
      otherReason: typeof o.otherReason === 'string' ? o.otherReason : '',
      smokedOtherProduct: smoked,
    };
  } catch {
    return emptyPrevBrandAnswerAnswers();
  }
}

export function writePrevBrandAnswerAnswers(data: PrevBrandAnswerAnswers): void {
  try {
    localStorage.setItem(PREV_BRAND_ANSWER_STORAGE_KEY, JSON.stringify(data));
  } catch {
    /* ignore */
  }
}

export function clearPrevBrandAnswerAnswers(): void {
  try {
    localStorage.removeItem(PREV_BRAND_ANSWER_STORAGE_KEY);
  } catch {
    /* ignore */
  }
}

function hasAnyReasonSelected(data: PrevBrandAnswerAnswers): boolean {
  return (
    data.taste.checked ||
    data.price.checked ||
    data.productFeatures.checked ||
    data.brandCredibility ||
    data.peersSmoking ||
    data.otherReason.trim().length > 0
  );
}

export function isPrevBrandAnswerComplete(): boolean {
  const data = readPrevBrandAnswerAnswers();
  if (!hasAnyReasonSelected(data)) return false;
  if (data.taste.checked && !data.taste.elaboration.trim()) return false;
  if (data.price.checked && !data.price.elaboration.trim()) return false;
  if (data.productFeatures.checked && !data.productFeatures.elaboration.trim()) return false;
  return data.smokedOtherProduct === 'yes' || data.smokedOtherProduct === 'no';
}

export type PrevBrandReasonType =
  | 'taste'
  | 'price'
  | 'productFeatures'
  | 'brandCredibility'
  | 'peersSmoking'
  | 'other';

export function getSelectedPrevBrandReason(
  data: PrevBrandAnswerAnswers
): { type: PrevBrandReasonType; elaboration: string } | null {
  if (data.taste.checked) {
    return { type: 'taste', elaboration: data.taste.elaboration };
  }
  if (data.price.checked) {
    return { type: 'price', elaboration: data.price.elaboration };
  }
  if (data.productFeatures.checked) {
    return { type: 'productFeatures', elaboration: data.productFeatures.elaboration };
  }
  if (data.brandCredibility) {
    return { type: 'brandCredibility', elaboration: '' };
  }
  if (data.peersSmoking) {
    return { type: 'peersSmoking', elaboration: '' };
  }
  const other = data.otherReason.trim();
  if (other.length > 0) {
    return { type: 'other', elaboration: other };
  }
  return null;
}
